import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { Panel } from '../../components/primitives/Panel';
import { captionLength, platformMeta } from '../platformMeta';
import { xMessages } from '../xMessages';
import { SocialPlatform } from '../types';
import { ComposerChannelTabs } from './ComposerChannelTabs';
import { ComposerCaptionField } from './ComposerCaptionField';
import { ComposerCopyToolbar } from './ComposerCopyToolbar';
import { ComposerChannelNote } from './ComposerChannelNote';
import { ComposerTimingSection } from './ComposerTimingSection';
import { ComposerDraft } from './useComposerDraft';
import { composerAiBlock, composerAiBlockMessages } from './composerAiBlock';
import { StudioActionFeedback } from '../studio/studioActionErrorText';
import { composerPostingModes } from './composerPostingModes';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Promote pillar; each part uses its own slice of it */
import styles from '../Promote.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ComposerCopyPanelProps {
  draft: ComposerDraft;
  isWriting: boolean;
  /** The result of the last failed AI write (QA finding B12). */
  aiError?: StudioActionFeedback | null;
  /** The active artist is the read-only example artist. */
  isDemo: boolean;
  /** The active artist is above the artist limit of the plan. */
  isReadOnly: boolean;
  onWriteMaster: () => void;
  onTailor: () => void;
}

/** The last step of the Composer: the copy and the hour. */
const ComposerCopyPanelComponent: React.FC<ComposerCopyPanelProps> = ({
  draft,
  isWriting,
  aiError,
  isDemo,
  isReadOnly,
  onWriteMaster,
  onTailor,
}) => {
  const intl = useIntl();
  const { tab, setTab, master, setMaster, overrides, captionFor, setOverride, resetOverride, platforms } = draft;

  const overridden = useMemo(() => Object.keys(overrides) as SocialPlatform[], [overrides]);

  const changeCopy = useCallback(
    (text: string) => (tab === 'master' ? setMaster(text) : setOverride(tab, text)),
    [setMaster, setOverride, tab]
  );

  const resetCopy = useCallback(() => {
    if (tab !== 'master') {
      resetOverride(tab);
    }
  }, [resetOverride, tab]);

  const masterLabel = intl.formatMessage({
    id: 'app.sml.promote.composer.tab.master',
    defaultMessage: 'Master copy',
  });

  const aiBlock = composerAiBlock({
    isDemo,
    isReadOnly,
    hasRelease: draft.release !== undefined,
    channelCount: platforms.length,
    isWriting,
  });
  const aiBlockReason = aiBlock === null ? undefined : intl.formatMessage(composerAiBlockMessages[aiBlock]);

  const value = tab === 'master' ? master : captionFor(tab);

  // The channel that goes out first is the one with the earliest slot, which is
  // not the same as the first one in the fixed platform order.
  const firstSlot = useMemo(
    () =>
      (Object.entries(draft.bestSlots) as [SocialPlatform, string][])
        .filter(([, slot]) => slot !== undefined)
        .sort(([, a], [, b]) => a.localeCompare(b))[0],
    [draft.bestSlots]
  );

  const postingModes = useMemo(() => composerPostingModes(draft.usable, platforms), [draft.usable, platforms]);

  return (
    <Panel>
      <ComposerChannelTabs platforms={platforms} overridden={overridden} active={tab} onChange={setTab} />

      <div className={styles.copyBody}>
        <ComposerCaptionField
          label={tab === 'master' ? masterLabel : platformMeta[tab].name}
          value={value}
          disabled={isWriting}
          onChange={changeCopy}
        />
        <ComposerCopyToolbar
          used={tab === 'master' ? value.length : captionLength(tab, value)}
          limit={tab === 'master' ? null : platformMeta[tab].captionLimit}
          aiBlockReason={aiBlockReason}
          isWriting={isWriting}
          aiError={aiError}
          onWriteMaster={onWriteMaster}
          onTailor={onTailor}
          onReset={tab !== 'master' && overrides[tab] !== undefined ? resetCopy : undefined}
        />
        {tab === 'x' ? (
          <p className={styles.aiHint} data-testid="composer-x-link-note">
            {intl.formatMessage(xMessages.linkNote)}
          </p>
        ) : null}
        {tab === 'master' ? null : <ComposerChannelNote platform={tab} slot={draft.bestSlots[tab]} />}
      </div>

      <div className={styles.copySection}>
        <ComposerTimingSection
          timing={draft.timing}
          pickedAt={draft.pickedAt}
          firstChannel={firstSlot === undefined ? undefined : platformMeta[firstSlot[0]].name}
          firstSlot={firstSlot?.[1]}
          othersCount={Math.max(platforms.length - 1, 0)}
          postingModes={postingModes}
          onChangeTiming={draft.setTiming}
          onChangePickedAt={draft.setPickedAt}
        />
      </div>
    </Panel>
  );
};

export const ComposerCopyPanel = TypedMemo(ComposerCopyPanelComponent);
